import { createMemo } from "solid-js"
import { createStore, produce } from "solid-js/store"
import { useServer } from "@/context/server"
import { createJSONStorage } from "@/storage/browser"
import { partitionPinnedItems, reorderPinnedSessionIds } from "./pinned-session-order"

type PinnedState = Record<string, string[]>

const PINNED_STATE_STORAGE_KEY = "mux.pinned-sessions.v1"

function normalizePinnedSessionIds(ids: unknown) {
  if (!Array.isArray(ids)) return []
  return [...new Set(ids.filter((id): id is string => typeof id === "string").map((id) => id.trim()).filter(Boolean))]
}

export function normalizePinnedState(value: unknown): PinnedState {
  if (!value || typeof value !== "object" || Array.isArray(value)) return {}
  return Object.fromEntries(
    Object.entries(value)
      .filter(([key]) => key.length > 0)
      .map(([key, ids]) => [key, normalizePinnedSessionIds(ids)])
      .filter(([, ids]) => ids.length > 0),
  )
}

const pinnedStorage = createJSONStorage<PinnedState>(PINNED_STATE_STORAGE_KEY, { decode: normalizePinnedState })

function readPinnedState(): PinnedState {
  return pinnedStorage.read({})
}

function persistPinnedState(state: PinnedState) {
  pinnedStorage.write(state, { removeWhen: (value) => Object.keys(value).length === 0 })
}

const [pinnedState, setPinnedState] = createStore<PinnedState>(readPinnedState())

function updatePinnedSessionIds(serverKey: string | undefined, ids: readonly string[]) {
  if (!serverKey) return
  const next = normalizePinnedSessionIds(ids)
  if (next.length) setPinnedState(serverKey, next)
  else setPinnedState(produce((draft) => { delete draft[serverKey] }))
  persistPinnedState(pinnedState)
}

export function getPinnedSessionIds(serverKey: string | undefined) {
  if (!serverKey) return []
  return pinnedState[serverKey] || []
}

export function setPinnedSessionIds(serverKey: string | undefined, ids: readonly string[]) {
  updatePinnedSessionIds(serverKey, ids)
}

/** A chat that continues another, such as a kept comparison branch, takes over its pin in place. */
export function replacePinnedSession(serverKey: string | undefined, fromID: string, toID: string) {
  const current = getPinnedSessionIds(serverKey)
  if (!current.includes(fromID)) return
  updatePinnedSessionIds(serverKey, current.map((id) => id === fromID ? toID : id))
}

export function usePinnedSessions() {
  const server = useServer()

  const pinnedSet = createMemo(() => new Set(getPinnedSessionIds(server.key)))

  const isPinned = (sessionId: string) => pinnedSet().has(sessionId)

  const togglePinned = (sessionId: string) => {
    const current = getPinnedSessionIds(server.key)
    updatePinnedSessionIds(
      server.key,
      current.includes(sessionId) ? current.filter((id) => id !== sessionId) : [...current, sessionId],
    )
  }

  const movePinned = (sessionId: string, targetId: string) => {
    updatePinnedSessionIds(server.key, reorderPinnedSessionIds(getPinnedSessionIds(server.key), sessionId, targetId))
  }

  const partition = <T extends { id: string }>(items: T[]) => partitionPinnedItems(items, getPinnedSessionIds(server.key))

  return { pinnedSet, isPinned, togglePinned, movePinned, partition }
}
